import { Box, Chip, Link } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { DataGridComponent } from "../common/DataGridComponent";
import {
  GridActionsCellItem,
  GridColDef,
  GridRowId,
  GridSortModel,
  getGridSingleSelectOperators,
} from "@mui/x-data-grid-pro";
import { useAppSelector, AppState, useAppDispatch } from "../../store";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useRoles } from "../../hooks/useRoles";
import { AlertDialog } from "../common/AlertDailog";
import {
  setIsEditRole,
  setOpenRolesModal,
  setSelectRoleData,
} from "../../store/slices/usersSlice";
import RolesModal from "./RolesModal";
import { useSnackbar } from "../../hooks/useSnackbar";
import { usePermissionWrapper } from "../../hooks/usePermissionWrapper";
import { initialPaginationModelValues } from "../../constants/paginationConstants";
import { debounce } from "lodash";

const RolesList: React.FC = () => {
  const openRolesModal = useAppSelector(
    (state: AppState) => state.usersSlice?.openRolesModal
  );
  const dispatch = useAppDispatch();
  const {
    getAllRoles,
    allRolesData,
    getSelectedRoles,
    allRolesIsLoading,
    allRolesError,
    allRolesIsError,
  } = useRoles();
  const [opendelete, setOpenDelete] = useState(false);
  const [rolesData, setRolesData] = useState([]);
  const [deleteRowData, setDeleteRowData] = useState<any>({});
  const [rowsCount, setRowsCount] = useState<number>(0);
  const [paginationModel, setPaginationModel] = useState(
    initialPaginationModelValues
  );
  const [filterObj, setFilterObject] = useState("");

  const { deleteRole, deleteRoleSuccess, deleteRoleIsError, deleteRoleError } =
    useRoles();
  const { showSuccess, handleError } = useSnackbar();
  const { checkPermission } = usePermissionWrapper();

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "code", sort: "asc" },
  ]);
  const handleSortModelChange = useCallback(
    debounce((model: GridSortModel) => {
      setSortModel(model);
    }, 300),
    []
  );

  useEffect(() => {
    if (deleteRoleSuccess) {
      getAllRoles({});
      setOpenDelete(false);
      showSuccess("Role deleted successfully", "role-delete-success");
    }
  }, [deleteRoleSuccess]);

  const OnEditRole = async (id: GridRowId) => {
    const res: any = await getSelectedRoles({ code: id, is_audit: true });
    if (res?.data) {
      const data = res?.data.role_details;
      dispatch(
        setSelectRoleData({
          code: data.code,
          name: data.name,
          description: data.description,
          modules: res?.data?.module_count,
          // permissions: [res?.data?.permissions?.assigned_permissions?.map((p:any)=>{...p,})],
          assigned_permissions: res.data.permissions?.assigned_permissions.map(
            (ele: any) => {
              return {
                ...ele,
                id: ele.code,
              };
            }
          ),
          unassigned_permissions:
            res.data.permissions?.unassigned_permissions.map((ele: any) => {
              return {
                ...ele,
                id: ele.code,
              };
            }),
        })
      );
      dispatch(setIsEditRole(true));
      dispatch(setOpenRolesModal(true));
    }
  };
  const columns: GridColDef[] = [
    {
      field: "code",
      headerName: "Role Code",
      minWidth: 160,
      flex: 1,
      renderCell: (params: any) =>
        checkPermission("EDIT_ROLE") ? (
          <Link onClick={() => OnEditRole(params.row.id)}>
            {params.row.code}
          </Link>
        ) : (
          params.row.code
        ),
    },
    {
      field: "name",
      headerName: "Role Name",
      minWidth: 160,
      flex: 1,
    },
    {
      field: "description",
      headerName: "Description",
      minWidth: 160,
      flex: 2,
    },
    {
      field: "is_active",
      headerName: "Active",
      width: 100,
      type: "singleSelect",
      valueOptions: [
        { value: true, label: "Yes" },
        { value: false, label: "No" },
      ],
      filterOperators: getGridSingleSelectOperators().filter(
        (operator) => operator.value === "is"
      ),
      renderCell: (params: any) =>
        params.row?.is_active ? (
          <Chip label="Yes" color="success" size="small" variant="outlined" />
        ) : (
          <Chip label="No" color="error" size="small" variant="outlined" />
        ),
    },
    {
      field: "actions",
      width: 100,
      align: "center",
      type: "actions",
      headerName: "Actions",
      sortable: false,
      getActions: (params) => [
        <GridActionsCellItem
          key={params.row.id}
          icon={<EditIcon />}
          label="Edit"
          onClick={() => OnEditRole(params.row.id)}
          showInMenu
          disabled={!checkPermission("EDIT_ROLE")}
        />,
        <GridActionsCellItem
          key={params.row.id}
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => {
            setDeleteRowData(params.row);
            setOpenDelete(true);
          }}
          showInMenu
          disabled={!checkPermission("DELETE_ROLE")}
        />,
      ],
    },
  ];

  useEffect(() => {
    getAllRoles({ is_audit: true });
  }, []);

  const getAllRolesFunc = (
    from: number,
    size: number,
    filterObj?: any,
    is_audit?: boolean
  ) => {
    getAllRoles({
      from,
      size,
      value: filterObj && filterObj?.quickFilter ? filterObj?.quickFilter : "",
      sort_by: sortModel?.[0]?.field,
      sort_order: sortModel?.[0]?.sort,
      data: filterObj && filterObj?.advanceFilter,
      is_audit: is_audit || false,
    });
  };

  useEffect(() => {
    if (allRolesData && allRolesData?.count > 0) {
      setRowsCount(allRolesData?.count);

      const data = allRolesData?.result?.map((ele: any) => {
        return {
          ...ele,
          id: ele.code,
        };
      });
      setRolesData(data);
    } else if (allRolesData?.count == 0) {
      setRowsCount(0);
      setRolesData([]);
    }
  }, [allRolesData]);

  useEffect(() => {
    if (paginationModel?.pageSize) {
      const { page, pageSize } = paginationModel;
      if (page * pageSize > rowsCount - 1) {
        getAllRolesFunc(0, pageSize, filterObj);
      } else {
        getAllRolesFunc(page * pageSize, pageSize, filterObj);
      }
    }
  }, [paginationModel, openRolesModal, filterObj]);

  useEffect(() => {
    if (allRolesIsError || deleteRoleIsError) {
      setRolesData([]);
      setRowsCount(0);
      const error: any = allRolesError ?? deleteRoleError;
      handleError(error);
    }
  }, [allRolesIsError, deleteRoleIsError]);

  return (
    <>
      <Box sx={{ width: "100%", height: "calc(100vh - 234px)" }}>
        <DataGridComponent
          columns={columns}
          rows={rolesData ?? []}
          showQuickFilter
          paginationMode="server"
          rowsCount={rowsCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          loading={allRolesIsLoading}
          filterMode="server"
          filterValues={(data: any) => setFilterObject(data)}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          exportFileName="Role_List"
        />
      </Box>
      <AlertDialog
        open={opendelete}
        title="Delete Role"
        description={`Are you sure you want to delete the role <strong>${deleteRowData?.code}</strong>?`}
        handleClose={() => setOpenDelete(false)}
        handleConfirm={() => deleteRole({ code: deleteRowData?.code })}
        confirmBtn="Yes"
        cancelBtn="No"
        maxWidth="xs"
      />
      {openRolesModal && (
        <RolesModal
          getAllRolesFunc={getAllRolesFunc}
          paginationModel={paginationModel}
        />
      )}
    </>
  );
};

export default RolesList;
